import React, { StrictMode, Suspense } from 'react'
import { Global, css } from '@emotion/core'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/react'
import { Router } from '@fs/zion-router'
import { sentryDSN, mergeSentryConfig, appPath } from '@fs/zion-config'
import { FeatureFlagsProvider, useFeatureFlag } from '@fs/zion-flags'
import { setAppLocales } from '@fs/zion-locale'
import { colors } from '@fs/zion-ui'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'
import RootsTechRoot from './RootsTechRoot'
import { URDU_LANGUAGE_FLAG } from './config/feature/flags'
import { getAppLocales, LoadingSpinner, ConferencePreviewRedirect } from './lib'
import redactSentryBreadcrumb from './lib/helpers/redactSentryBreadcrumb'
import { BrightspotConfigProvider, RootstechFlagsProvider, ThemeProvider } from './providers'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
// import { RTCProvider } from './state/RTCStore'

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      ignoreErrors: [
        'AbortError',
        'SecurityError: The operation is insecure.',
        // This is probably coming from an in-app webview. I see a lot of these coming from android
        /Java object is gone/,
        // Third-party code injected by the browser environment (in-app WebView bridge / extension)
        // calls a `postEvent` host method that doesn't exist. Not RootsTech code — see RT-75 / ROOTSTECH-306.
        // memories-react filters the same error as third-party noise.
        /invoking postEvent: Method not found/,
        // These should be included in strictFiltering, but some were slipping by, so ignore common HTTP status-code error messages here too.
        /status(?: code)?\D*(?:400|401|403|5\d{2})\b/i,
      ],
      denyUrls: [/bitmovin-player/],
      beforeBreadcrumb: redactSentryBreadcrumb,
      sampleRate: 0.2,
      strictFiltering: true,
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// makes the background dark before the app loads. Setting the theme is the "correct" way to do it once the app loads
const htmlOverrideCss = css`
  html {
    background-color: ${colors.gray00};
  }
`

// add any app logic here, languages and translations are configured and available here
const FrontierWrapper = (): JSX.Element => {
  // An unresolved flag reports `control`, so Urdu stays out of the picker until Split answers.
  const urduLanguageFlag = useFeatureFlag(URDU_LANGUAGE_FLAG)
  setAppLocales(getAppLocales(Boolean(urduLanguageFlag?.isOn)))

  return (
    <Router basename={base}>
      <ConferencePreviewRedirect>
        <RootstechFlagsProvider>
          <ThemeProvider>
            <BrightspotConfigProvider>
              <RootsTechRoot>
                <App />
              </RootsTechRoot>
            </BrightspotConfigProvider>
          </ThemeProvider>
        </RootstechFlagsProvider>
      </ConferencePreviewRedirect>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = (): JSX.Element => {
  return (
    <>
      <Global styles={htmlOverrideCss} />
      <StrictMode>
        <Suspense fallback={<LoadingSpinner />}>
          <FeatureFlagsProvider mockFlags={mockFlags} enableRealtime={false}>
            <FrontierWrapper />
          </FeatureFlagsProvider>
        </Suspense>
      </StrictMode>
    </>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
